import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Brand lookup for every depot. Populated from `/depots` on login. The PDF
// exporters read from this (via `getDepotBrand(code)` below) so an invoice
// generated against TG-LFW renders with the Lom\u00e9 letterhead/address.
let _depotBrands = {};
export function setDepotBrands(brandsByCode) {
  _depotBrands = brandsByCode || {};
}
export function getDepotBrand(code) {
  const up = (code || "").toUpperCase().trim();
  return _depotBrands[up] || null;
}
// Every request carries an `X-Depot` header matching the depot currently
// picked in the top-nav depot switcher. The AuthContext sets this via
// `setDepot()` on login + whenever the user flips the picker. Empty / "ALL"
// means "every depot I have access to" (admins + any multi-depot user).
let _currentDepot = "";
export function setApiDepot(code) {
  _currentDepot = (code || "").toUpperCase().trim();
}
api.interceptors.request.use((config) => {
  if (_currentDepot && _currentDepot !== "ALL") {
    config.headers = { ...(config.headers || {}), "X-Depot": _currentDepot };
  }
  return config;
});

// ─── Silent refresh on 401 ──────────────────────────────────────────────────
// Gate clerks may sit on a single page for 30+ minutes waiting for a truck.
// When their 60-minute access token expires they used to see "Not authenticated"
// mid-form-save with no recovery. The interceptor below transparently calls
// /auth/refresh once per failed request and retries — so they never notice.
// Only kicks them to /login when even the refresh fails (real session loss).
let refreshPromise = null;

function attemptRefresh() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const original = err?.config;
    const status = err?.response?.status;
    // Don't loop: never retry the refresh endpoint itself or /auth/login.
    const isAuthEndpoint = typeof original?.url === "string" &&
      (original.url.includes("/auth/refresh") || original.url.includes("/auth/login"));
    if (status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      try {
        await attemptRefresh();
        return api(original);
      } catch {
        // Refresh failed → genuine session loss. Send them to /login but only
        // if we're not already there (avoid loops on the public landing page).
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.assign("/login");
        }
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}
